import { useToast } from '@chakra-ui/react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { ApiClient, useApiClient } from 'api/context-provider/useApiClient';
import { InferFetchData, throwOnError } from 'api/helpers';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import { useEffect } from 'react';

export type AutomationStatus = {
  id: number;
  class: string;
  name: string;
  description: string | null;
  status: 0 | 1 | 2;
  automation_percentage: number;
  updated_at: string;
};

async function getAutomations(apiClient: ApiClient) {
  return apiClient.GET('/op/v2/automations');
}

type AutomationsResponse = InferFetchData<typeof getAutomations>;

function getKey() {
  return ['Automations'] as const satisfies QueryKey;
}

export default function useAutomationStatusQuery() {
  const apiClient = useApiClient();
  const toast = useToast();

  const query = useQuery<AutomationsResponse>({
    queryKey: getKey(),
    queryFn: () => throwOnError(getAutomations(apiClient)),
    notifyOnChangeProps: ['data', 'isFetching', 'error'],
    retry: false,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (query.isError) {
      manageErrorResponse(toast, query.error as any);
    }
  }, [query.error, query.isError, toast]);

  return query;
}

useAutomationStatusQuery.getKey = getKey;
